<template>
  <div class="w-full space-y-6">
    <div class="flex items-center justify-between">
      <ChallengeProgressBar
          :current="currentQuestionOrder"
          :total="totalQuestions"
          class="flex-1 md:mr-6"
      />
      <ChallengeTimer
          v-if="attempt.expires_at"
          :expires-at="attempt.expires_at"
          :initial-seconds="attempt.remaining_seconds"
          @time-up="handleTimeUp"
      />
    </div>

    <div class="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300 p-8">
      <div class="grid gap-8 md:grid-cols-2">
        <div>
          <div class="mb-6">
            <p v-if="question.description" class="text-zinc-600  text-lg font-medium mb-4">{{ question.description }}</p>
            <YoutubePlayer
                v-if="question.question_type === 3 && question.media_provider === 'youtube'"
                :videoId="question.video_id"
                height="344px" />

            <CloudflarePlayer
                v-if="question.question_type === 3 && question.media_provider === 'cloudflare'"
                :videoId="question.video_id"
                height="344px" />
            <img
                v-if="question.question_type === 2"
                class="h-auto md:h-[388px] w-full md:w-[500px] mx-auto overflow-hidden rounded-2xl"
                :src="questionMediaContent"
                :alt="__('Challenge preview image')"
            />
          </div>
          <div class="flex items-start md:mb-4">
            <div class="w-11 h-11 rounded-lg border-2 border-zinc-200 flex items-center justify-center mr-3 py-2 px-2.5">
              <span class="text-lg font-semibold text-gray-700">Q{{ currentQuestionOrder }}</span>
            </div>
            <div>
              <h2 class="text-lg font-medium text-gray-900 my-2 md:mb-2">{{ question.title }}</h2>
            </div>
          </div>
        </div>

        <div>
          <div class="flex items-center justify-between mb-4">
            <p class="text-lg font-medium text-zinc-800">{{ question.answer_type === 3 ? __('Enter your answer here.') : __('Choose the correct answer') }}</p>
            <div v-if="(question.answer_type === 1 || question.answer_type === 2) && challenge.enable_type_hint" class="inline-flex items-center px-3 py-1.5 rounded-xl text-sm  text-zinc-800 border-2 border-secondary">
              {{ answerTypeLabel }}
            </div>
          </div>
          <div class="space-y-3">
            <SingleChoiceAnswer
                v-if="question.answer_type === 1"
                :answers="question.answers"
                :selected-answer-id="selectedAnswerId"
                @select="handleAnswerSelect"
            />
            <MultipleChoiceAnswer
                v-else-if="question.answer_type === 2"
                :answers="question.answers"
                :selected-answer-ids="selectedAnswerIds"
                @select="handleMultipleAnswerSelect"
            />
            <TextAnswer
                v-else-if="question.answer_type === 3"
                :value="textAnswer"
                @input="handleTextAnswerInput"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col-reverse md:flex-row md:justify-between gap-3 w-full">
      <div v-if="!isFirstQuestion" class="flex w-full md:w-auto">
        <button
            type="button"
            @click="handleBack"
            :disabled="navigating"
            class="inline-flex items-center py-2 hover-lift-no-shadow text-gray-700 transition-colors"
            variant="ghost-no-padding"
        >
          <Icon name="arrow_back_ios" class="w-5 h-5  text-xl text-zinc-800" />
          {{ navigating ? __('Loading...') : __('Back to previous question') }}
        </button>
      </div>

      <div v-else class="hidden md:block"></div>

      <div class="flex gap-2 md:gap-3 w-full md:w-auto">
        <button
            type="button"
            @click="handleSaveForLater"
            :disabled="saving"
            class="md:w-auto px-6 py-2 rounded-md border-2 border-primary text-primary hover:bg-primary/10 transition-colors disabled:opacity-50 flex-1 md:flex-none"
        >
          {{ saving ? __('Saving...') : __('Save For Later') }}
        </button>
        <button
            type="button"
            @click="handleContinue"
            :disabled="!canContinue || submitting"
            class="md:w-auto px-6 py-2 rounded-md bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50 flex-1 md:flex-none"
        >
          {{ submitting ? (isLastQuestion ? __('Submitting...') : __('Saving...')) : (isLastQuestion ? __('Submit') : __('Continue')) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, computed, onMounted, onBeforeUnmount} from 'vue';
import { useToast } from 'vue-toastification';
import axios from 'axios';
import { __ } from '@/helpers';
import ChallengeProgressBar from './components/ChallengeProgressBar.vue';
import ChallengeTimer from './components/ChallengeTimer.vue';
import SingleChoiceAnswer from './components/SingleChoiceAnswer.vue';
import MultipleChoiceAnswer from './components/MultipleChoiceAnswer.vue';
import TextAnswer from './components/TextAnswer.vue';
import YoutubePlayer from "@/pages/candidate/challenges/components/YoutubePlayer.vue";
import CloudflarePlayer from "@/pages/candidate/challenges/components/CloudflarePlayer.vue";
import {Icon} from "@/components/block/icon";

const props = defineProps({
  challenge: Object,
  attempt: Object,
  question: Object,
  savedAnswer: Object,
  currentOrder: Number,
  previousQuestionId: [Number, String],
});

const questionMediaContent = computed(() => {
  return props.question.media_content
      ? `/uploads/${props.question.media_content}`
      : '/images/placeholder_challenge.png';
});

const saving = ref(false);
const submitting = ref(false);
const toast = useToast();
const navigating = ref(false);

const selectedAnswerId = ref(props.savedAnswer?.answer_ids?.[0] || null);
const selectedAnswerIds = ref(props.savedAnswer?.answer_ids || []);
const textAnswer = ref(props.savedAnswer?.text_answer || '');

const currentQuestionOrder = computed(() => props.currentOrder || 1);
const totalQuestions = computed(() => props.challenge.questions_count);

const windowWidth = ref(window.innerWidth);

const handleResize = () => {
  windowWidth.value = window.innerWidth;
}

onMounted(() => {
  window.addEventListener("resize", handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize)
})

const answerTypeLabel = computed(() => {
  switch (props.question.answer_type) {
    case 1: return __('Single Choice');
    case 2: return __('Multiple Choice');
    case 3: return __('Text Answer');
    default: return '';
  }
});

const canContinue = computed(() => {
  if (props.question.answer_type === 1) {
    return selectedAnswerId.value !== null;
  } else if (props.question.answer_type === 2) {
    return selectedAnswerIds.value.length > 0;
  } else if (props.question.answer_type === 3) {
    return textAnswer.value.trim().length > 0;
  }
  return false;
});

const isLastQuestion = computed(() => {
  return currentQuestionOrder.value === totalQuestions.value;
});

const isFirstQuestion = computed(() => {
  return currentQuestionOrder.value === 1;
})

const handleAnswerSelect = (answerId) => {
  selectedAnswerId.value = answerId;
};

const handleMultipleAnswerSelect = (answerIds) => {
  selectedAnswerIds.value = answerIds;
};

const handleTextAnswerInput = (value) => {
  textAnswer.value = value;
};

const handleBack = async () => {
  navigating.value = true;

  try {
    const response = await axios.post(route('redesign.candidate.challenges.navigate', {
      challenge: props.challenge.id,
      attempt: props.attempt.id
    }), {
      current_order: currentQuestionOrder.value
    });
    if (response.data.redirect) {
      window.location.href = response.data.redirect;
    }
  } catch (error) {
    toast.error(error.response?.data?.message || __('Failed to navigate to previous question'));
    navigating.value = false;
  }
};

const handleSaveForLater = async () => {
  saving.value = true;
  try {
    const answerData = prepareAnswerData();

    let payload = {};
    if (answerData) {
      payload.answers = {
        [props.question.id]: answerData
      }
    }
    const response = await axios.post(route('redesign.candidate.challenges.save.for.later', {
      attempt: props.attempt.id
    }), payload);

    toast.success(response.data.message);
    window.location.href = response.data.redirect;
  } catch (error) {
    toast.error(error.response?.data?.message || __('Failed to save progress'));
  }
};

const handleContinue = async () => {
  if (!canContinue.value) return;

  submitting.value = true;
  try {
    const answerData = prepareAnswerData();
    const response = await axios.post(route('redesign.candidate.challenges.answer', {
      challenge: props.challenge.id,
      attempt: props.attempt.id,
      question: props.question.id
    }), {
      answer: answerData
    });

    if (response.data.redirect) {
      window.location.href = response.data.redirect;
    }
  } catch (error) {
    toast.error(error.response?.data?.message || __('Failed to save answer'));
  } finally {
    submitting.value = false;
  }
};

const handleTimeUp = async () => {
  toast.warning(__('Time is up! Submitting your challenge...'));

  submitting.value = true;
  try {
    const answerData = prepareAnswerData();
    if (answerData && canContinue.value) {
      await axios.post(route('redesign.candidate.challenges.answer', {
        challenge: props.challenge.id,
        attempt: props.attempt.id,
        question: props.question.id
      }), {
        answer: answerData
      });
    }

    const response = await axios.post(route('redesign.candidate.challenges.submit', {
      challenge: props.challenge.id,
      attempt: props.attempt.id
    }));

    if (response.data.redirect) {
      toast.success(response.data.message || __('Challenge submitted successfully'));
      setTimeout(() => {
        window.location.href = route('challenges.index');
      }, 2000);
    }
  } catch (error) {
    toast.error(error.response?.data?.message || __('Failed to submit challenge'));
  } finally {
    submitting.value = false;
  }
};

const prepareAnswerData = () => {
  if (props.question.answer_type === 1) {
    return selectedAnswerId.value ? { answer_ids: [selectedAnswerId.value] } : null;
  } else if (props.question.answer_type === 2) {
    return selectedAnswerIds.value.length > 0 ? { answer_ids: selectedAnswerIds.value } : null;
  } else if (props.question.answer_type === 3) {
    return textAnswer.value.trim() ? { text_answer: textAnswer.value } : null;
  }
  return null;
};

const handleBeforeUnload = () => {
  const data = new FormData();
  data.append('exit_type', 'abandoned');

  const tokenMeta = document.querySelector('meta[name="csrf-token"]');
  if (tokenMeta) {
    data.append('_token', tokenMeta.getAttribute('content'));
  }

  navigator.sendBeacon(
      route('redesign.candidate.challenges.exit', { attempt: props.attempt.id }),
      data
  );
};

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload);
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
});
</script>